import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, MessageSquare, Search as SearchIcon, Phone, Users, Star, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from "recharts";

const RANGES = [{ d: 7, l: "7d" }, { d: 30, l: "30d" }, { d: 90, l: "90d" }];

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-[#E8E6DF] rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#5C6660] mb-2"><Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
      <div className="text-3xl font-heading font-medium text-[#1C211F]">{value?.toLocaleString() ?? 0}</div>
    </div>
  );
}

export function AnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/analytics", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !data) return <div className="py-16 text-center text-[#5C6660]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const t = data.totals;

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-[#4A534E]">Traffic and engagement across the site and each listing.</p>
        <div className="flex gap-1 bg-[#EFEEE8] rounded-xl p-1">
          {RANGES.map((r) => (
            <button key={r.d} onClick={() => setDays(r.d)} data-testid={`range-${r.d}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${days === r.d ? "bg-white text-[#1A365D] shadow-sm" : "text-[#5C6660]"}`}>
              {r.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Unique visitors" value={t.unique_visitors} testid="kpi-visitors" />
        <Kpi icon={Eye} label="Page views" value={t.page_views} testid="kpi-pageviews" />
        <Kpi icon={SearchIcon} label="Searches" value={t.searches} testid="kpi-searches" />
        <Kpi icon={Eye} label="Listing views" value={t.facility_views} testid="kpi-listingviews" />
        <Kpi icon={MessageSquare} label="Inquiry opens" value={t.inquiry_clicks} testid="kpi-inquiryclicks" />
        <Kpi icon={TrendingUp} label="Inquiries sent" value={t.inquiries} testid="kpi-inquiries" />
        <Kpi icon={Phone} label="Contact clicks" value={t.contact_clicks} testid="kpi-contacts" />
        <Kpi icon={Star} label="Saves" value={t.favorites} testid="kpi-favorites" />
      </div>

      <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
        <h3 className="font-heading text-lg font-medium mb-4">Traffic over time</h3>
        {data.series.length === 0 ? (
          <p className="text-sm text-[#5C6660] py-12 text-center">No activity yet in this range.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.series} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0077B6" stopOpacity={0.3} /><stop offset="100%" stopColor="#0077B6" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1A365D" stopOpacity={0.3} /><stop offset="100%" stopColor="#1A365D" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEEE8" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C6660" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5C6660" }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="page_view" name="Page views" stroke="#0077B6" fill="url(#g1)" strokeWidth={2} />
              <Area type="monotone" dataKey="facility_view" name="Listing views" stroke="#1A365D" fill="url(#g2)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
          <h3 className="font-heading text-lg font-medium mb-4">Top search locations</h3>
          {data.top_locations.length === 0 ? (
            <p className="text-sm text-[#5C6660] py-8 text-center">No searches yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(140, data.top_locations.length * 34)}>
              <BarChart data={data.top_locations} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="location" width={120} tick={{ fontSize: 11, fill: "#4A534E" }} />
                <Tooltip />
                <Bar dataKey="count" name="Searches" fill="#0077B6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6">
          <h3 className="font-heading text-lg font-medium mb-4">Top facilities</h3>
          {data.top_facilities.length === 0 ? (
            <p className="text-sm text-[#5C6660] py-8 text-center">No listing activity yet.</p>
          ) : (
            <div className="overflow-x-auto" data-testid="top-facilities-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-[#5C6660] border-b border-[#E8E6DF]">
                  <th className="py-2 pr-2">Facility</th><th className="py-2 px-1 text-right">Views</th><th className="py-2 px-1 text-right">Opens</th><th className="py-2 px-1 text-right">Inquiries</th><th className="py-2 pl-1 text-right">Contacts</th>
                </tr></thead>
                <tbody>
                  {data.top_facilities.map((f) => (
                    <tr key={f.facility_id} className="border-b border-[#F2F1EC]">
                      <td className="py-2 pr-2"><div className="font-medium text-[#1C211F] truncate max-w-[180px]">{f.name}</div><div className="text-xs text-[#5C6660] capitalize">{f.tier}</div></td>
                      <td className="py-2 px-1 text-right font-mono">{f.views}</td>
                      <td className="py-2 px-1 text-right font-mono">{f.inquiry_clicks}</td>
                      <td className="py-2 px-1 text-right font-mono">{f.inquiries}</td>
                      <td className="py-2 pl-1 text-right font-mono">{f.contact_clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
